
import React from 'react';
import { formatUsPhone, maskUsPhone } from 'utilities/formatPhone';
import '../../../styles/mastercertificate.css';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { handleApiResponse } from 'utilities/apiUtils';

interface PhoneBookProps {
  visible: boolean;
  onHide: () => void;
  onSelect?: (data: { email: string; fax: string }) => void;
}

const contactTypeOptions = [
  { label: 'Carrier', value: 'carrier' },
  { label: 'MGA', value: 'mga' },
  { label: 'Finance Company', value: 'financeCompany' },
  { label: 'Certificate Holder', value: 'certificateHolder' },
];

const PhoneBook: React.FC<PhoneBookProps> = ({ visible, onHide, onSelect }) => {
  const [contactType, setContactType] = React.useState<string>('');
  const [selectedEntity, setSelectedEntity] = React.useState<any>(null);
  const [entitySuggestions, setEntitySuggestions] = React.useState<{ label: string; value: string; _raw?: any }[]>([]);
  const [email, setEmail] = React.useState('');
  const [fax, setFax] = React.useState('');
  const [loadingEntity, setLoadingEntity] = React.useState(false);

  React.useEffect(() => {
    if (selectedEntity && selectedEntity._raw) {
      const entity = selectedEntity._raw;
      setEmail(entity.eMail || entity.email || '');
      setFax(formatUsPhone(entity.fax || entity.phoneNumber || ''));
    }
  }, [selectedEntity]);

  if (!visible) return null;

  const getEntityLabel = () => {
    switch (contactType) {
      case 'carrier':
        return 'Carrier';
      case 'mga':
        return 'MGA';
      case 'financeCompany':
        return 'Finance Company';
      case 'certificateHolder':
        return 'Certificate Holder';
      case 'insuredContact':
        return 'Insured Contact';
      default:
        return 'Entity';
    }
  };

  const fetchEntitySuggestions = async (query: string) => {
    if (!contactType || query.length < 2) return;

    setLoadingEntity(true);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      
      let url = '';
      switch (contactType) {
        case 'carrier':
          url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/carriers?top=20&search=${encodeURIComponent(query)}`;
          break;
        case 'mga':
          url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/underwriters?top=30&search=${encodeURIComponent(query)}`;
          break;
        case 'financeCompany':
          url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/finance-companies?top=10&active=true&search=${encodeURIComponent(query)}`;
          break;
        case 'certificateHolder':
          url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-holders?top=10&search=${encodeURIComponent(query)}`;
          break;
        default:
          return;
      }

      const response = await fetch(url, {
        headers: {
          'NCToken': ncToken,
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
        },
      });

      await handleApiResponse(response);
      const data = await response.json();
      
      const suggestions = (data.value || []).map((item: any) => ({
        label: item.name || item.companyName || '-',
        value: item.databaseId || item.id,
        _raw: item,
      }));
      
      setEntitySuggestions(suggestions);
    } catch (error) {
      console.error('Error fetching entity suggestions:', error);
      setEntitySuggestions([]);
    } finally {
      setLoadingEntity(false);
    }
  };

  const handleEntityComplete = (event: any) => {
    const query = event.query;
    fetchEntitySuggestions(query);
  };

  const handleContactTypeChange = (value: string) => {
    setContactType(value);
    setSelectedEntity(null);
    setEntitySuggestions([]);
    setEmail('');
    setFax('');
  };

  const handleSelect = () => {
    if (onSelect) {
      onSelect({ email, fax });
    }
    onHide();
  };

  return (
    <div className="masterCert__popupOverlay">
      <div className="masterCert__popup" style={{ maxWidth: 720 }}>
        <div className="masterCert__popupHeader">
          <span>Phone Book</span>
          <button className="masterCert__popupCloseBtn" onClick={onHide}>×</button>
        </div>
        <div className="carrierDialog__card" style={{ margin: '24px 0' }}>
          <div className="carrierDialog__rowFlex">
            <div className="carrierDialog__fieldFlex">
              <div className="carrierDialog__label">Contact Type</div>
              <div className="masterCert__dateCell">
                <Dropdown
                  value={contactType}
                  options={contactTypeOptions}
                  onChange={(e) => handleContactTypeChange(e.value)}
                  placeholder="Select contact type"
                  style={{ width: 300 }}
                  emptyMessage="No available options"
                />
              </div>
            </div>
            {contactType && contactType !== 'insuredContact' && (
              <div className="carrierDialog__fieldFlex">
                <div className="carrierDialog__label">{getEntityLabel()}</div>
                <div className="masterCert__dateCell">
                  <AutoComplete
                    value={selectedEntity}
                    suggestions={entitySuggestions}
                    completeMethod={handleEntityComplete}
                    onChange={(e) => setSelectedEntity(e.value)}
                    placeholder={`Search ${getEntityLabel().toLowerCase()}`}
                    field="label"
                    style={{ width: 300 }}
                    inputStyle={{ width: 300 }}
                    disabled={!contactType}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="carrierDialog__rowFlex">
            <div className="carrierDialog__fieldFlex">
              <div className="carrierDialog__label">Email</div>
              <InputText maxLength={100}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter mailing address"
                className="carrierDialog__input"
                style={{ width: '100%' }}
              />
            </div>
            <div className="carrierDialog__fieldFlex">
              <div className="carrierDialog__label">Fax</div>
              <InputText maxLength={20}
                value={fax}
                onChange={e => setFax(maskUsPhone(e.target.value))}
                placeholder="Enter fax number"
                className="carrierDialog__input"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
        <div className="carrierDialog__footer">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text carrierDialog__cancelBtn"
            onClick={onHide}
          />
          <Button
            type="button"
            label="Select"
            className="p-button-primary carrierDialog__saveBtn"
            onClick={handleSelect}
          />
        </div>
      </div>
    </div>
  );
};

export default PhoneBook;
